import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Building2,
  Users,
  UserCheck,
  MessageSquare,
  ClipboardCheck,
  Users2,
  Briefcase,
  ChevronDown,
  Linkedin,
  Instagram,
  Youtube,
  Settings,
  FileCheck,
  Code2,
  Target,
  Activity,
  Receipt,
  Calculator,
  PanelLeftClose,
  PanelLeft,
  LogOut,
  Heart,
  Trophy,
  Gauge,
  Tv,
  BarChart3,
  PenLine,
  Rocket,
  AlertTriangle,
  Sparkles,
  Zap,
  TrendingUp,
  FileBarChart,
  Layers,
  CircleDot,
  DollarSign,
  Clock,
  Grid3X3,
  CalendarDays,
  Download,
  UsersRound,
  ClipboardList,
  FolderKanban,
  Globe,
  Shield,
  Lock,
  TestTube2,
  FileText,
} from "lucide-react";
import { cn } from "@/modules/shared/lib/utils";
import { Button } from "@/modules/shared/components/ui/button";
import { useState, useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useBranding } from "@/modules/shared/hooks/useBranding";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/modules/shared/components/ui/collapsible";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/modules/shared/components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/modules/shared/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/modules/shared/components/ui/avatar";
import { usePortalAuth } from "@/modules/portal/hooks/usePortalAuth";
import { useAuth } from "@/modules/shared/hooks/useAuth";
import { db } from "@/modules/shared/lib/db-client";
import { useUserPermissions } from "@/modules/shared/hooks/usePermissions";
import { useUserRole } from "@/modules/shared/hooks/useUserRole";
import { useModuleAccess, getInactiveModules, type ModuleKey } from "@/modules/shared/hooks/useModuleAccess";
import { ModuleSwitcher } from "@/modules/shared/components/layout/ModuleSwitcher";

import { usePlatformAdmin } from "@/modules/shared/hooks/usePlatformAdmin";
import { useTrialStatus } from "@/modules/shared/hooks/useTrialStatus";
import { useOrganization } from "@/modules/shared/hooks/useOrganization";
import { log } from "@/modules/shared/lib/observability";

// ==========================================
// PURE SIDEBAR FILTERING HELPERS
// ==========================================

/**
 * Check if an item belongs to the given current module or shell.
 * Items without a module property are considered shell items (always visible).
 * Items with module === currentModule are visible.
 * All other module items are hidden.
 */
export function isCurrentModuleItem(
  item: { module?: ModuleKey },
  currentModule: ModuleKey,
): boolean {
  if (!item.module) return true; // shell / no-module items always visible
  return item.module === currentModule;
}

/**
 * Check if a section belongs to the given current module or shell.
 */
export function isCurrentModuleSection(
  section: { module?: ModuleKey },
  currentModule: ModuleKey,
): boolean {
  if (!section.module) return true; // shell sections always visible
  return section.module === currentModule;
}

/**
 * Filter a list of items/sections to only those belonging to the current module or shell.
 */
export function filterItemsByCurrentModule<T extends { module?: ModuleKey }>(
  items: T[],
  currentModule: ModuleKey,
): T[] {
  return items.filter((item) => isCurrentModuleItem(item, currentModule));
}

// ==========================================
// MENU STRUCTURE - Organized by workflow
// ==========================================

interface MenuItem {
  name: string;
  href: string;
  icon: React.ElementType;
  featureKey?: string;
  module?: ModuleKey;
  exact?: boolean;
  badge?: string;
  comingSoon?: boolean;
}

interface MenuSection {
  title: string;
  icon: React.ElementType;
  module?: ModuleKey;
  items: MenuItem[];
}

// Dashboard - Standalone
const dashboardItem: MenuItem = {
  name: "Dashboard",
  href: "/dashboard",
  icon: LayoutDashboard,
  featureKey: "visao_geral",
  exact: true,
};

// GESTÃO Section - Cadastros (mixes sales+talent)
const gestaoSection: MenuSection = {
  title: "Gestão",
  icon: Layers,
  items: [
    { name: "Clientes", href: "/clientes", icon: Building2, featureKey: "clientes", module: "sales" },
    { name: "Time", href: "/profissionais", icon: Users, featureKey: "profissionais", module: "talent" },
  ],
};

// RECRUTAMENTO Section
const recrutamentoSection: MenuSection = {
  title: "Recrutamento",
  icon: Briefcase,
  module: "talent",
  items: [
    { name: "Avaliações", href: "/recrutamento", icon: ClipboardList, featureKey: "recrutamento", module: "talent" },
  ],
};

// Vagas sub-menu
const vagasItems: MenuItem[] = [
  { name: "Vagas", href: "/vagas", icon: Briefcase, exact: true, featureKey: "vagas", module: "talent" },
  { name: "Modelos", href: "/vagas/modelos", icon: FileText, featureKey: "vagas", module: "talent" },
  { name: "Página de Carreiras", href: "/carreiras/editor", icon: Globe, featureKey: "vagas", module: "talent" },
  { name: "Configurações", href: "/vagas/configuracoes", icon: Settings, featureKey: "vagas", module: "talent" },
];

// OPERAÇÕES Section
const operacoesSection: MenuSection = {
  title: "Operações",
  icon: FolderKanban,
  module: "operations",
  items: [
    { name: "Projetos", href: "/projetos", icon: FolderKanban, featureKey: "projetos", module: "operations" },
    { name: "Squads", href: "/squads", icon: Users2, featureKey: "squad", module: "operations" },
    { name: "SpecQA", href: "/specqa", icon: TestTube2, featureKey: "specqa", module: "operations" },
  ],
};

// ACOMPANHAMENTO Section
const acompanhamentoSection: MenuSection = {
  title: "Acompanhamento",
  icon: Activity,
  module: "talent",
  items: [
    { name: "Performance", href: "/checkins", icon: MessageSquare, featureKey: "checkins", module: "talent" },
    { name: "Planos de Ação", href: "/planos-de-acao", icon: Target, featureKey: "planos_acao", module: "talent" },
    { name: "PDI & Feedbacks", href: "/pdi", icon: FileCheck, featureKey: "pdi", module: "talent" },
    { name: "Resumo Mensal", href: "/resumo-mensal", icon: ClipboardCheck, featureKey: "resumo_mensal", module: "talent" },
  ],
};

// Cultura sub-menu
const culturaItems: MenuItem[] = [
  { name: "Dashboard", href: "/cultura", icon: Heart, exact: true, featureKey: "cultura", module: "talent" },
  { name: "Configurar", href: "/cultura/configurar", icon: Settings, featureKey: "cultura_config", module: "talent" },
];

// Timesheet sub-menu
const timesheetItems: MenuItem[] = [
  { name: "Minhas Horas", href: "/timesheet", icon: Clock, exact: true, featureKey: "timesheet", module: "operations" },
  { name: "Semanal", href: "/timesheet/semanal", icon: Grid3X3, featureKey: "timesheet_weekly", module: "operations" },
  { name: "Calendário", href: "/timesheet/calendario", icon: CalendarDays, featureKey: "timesheet_calendar", module: "operations" },
];

// Timesheet sub-menu for managers
const timesheetManagerItems: MenuItem[] = [
  { name: "Minhas Horas", href: "/timesheet", icon: Clock, exact: true, featureKey: "timesheet", module: "operations" },
  { name: "Semanal", href: "/timesheet/semanal", icon: Grid3X3, featureKey: "timesheet_weekly", module: "operations" },
  { name: "Calendário", href: "/timesheet/calendario", icon: CalendarDays, featureKey: "timesheet_calendar", module: "operations" },
  { name: "All Times", href: "/timesheet/all-times", icon: UsersRound, featureKey: "timesheet_all_times", module: "operations" },
];

// RESULTADOS Section
const resultadosSection: MenuSection = {
  title: "Resultados",
  icon: TrendingUp,
  module: "talent",
  items: [
    { name: "Painel Gestores", href: "/dashboard-engajamento", icon: Activity, featureKey: "painel_gestores", module: "talent" },
  ],
};

// COMERCIAL Section
const comercialSection: MenuSection = {
  title: "Comercial",
  icon: Receipt,
  module: "sales",
  items: [
    { name: "Cockpit Comercial", href: "/revenue", icon: TrendingUp, featureKey: "minha_carteira", module: "sales" },
    { name: "Minha Carteira", href: "/minha-carteira", icon: Briefcase, featureKey: "minha_carteira", module: "sales" },
    { name: "Rate Card", href: "/rate-card", icon: Receipt, featureKey: "rate_card", module: "sales" },
    { name: "Calculadora", href: "/calculadora-comercial", icon: Calculator, featureKey: "calculadora", module: "sales" },
  ],
};

// FINANCEIRO Section
const financeiroSection: MenuSection = {
  title: "Financeiro",
  icon: DollarSign,
  module: "sales",
  items: [
    { name: "Visão Financeira", href: "/financeiro", icon: DollarSign, featureKey: "financeiro", module: "sales" },
    { name: "Reembolsos", href: "/reembolsos", icon: Receipt, featureKey: "reembolsos", module: "talent" },
  ],
};

// Portal do Cliente sections
const portalMenuSections = [
  {
    title: "Visão Geral",
    items: [
      { name: "Dashboard", href: "/portal", icon: LayoutDashboard, exact: true },
      { name: "Resumo Semanal", href: "/portal/resumo", icon: Sparkles },
      { name: "Radar de Riscos", href: "/portal/riscos", icon: AlertTriangle },
    ]
  },
  {
    title: "Entregas",
    items: [
      { name: "Timeline", href: "/portal/timeline", icon: Rocket },
      { name: "Performance", href: "/portal/performance", icon: Activity },
    ]
  },
  {
    title: "Equipe",
    items: [
      { name: "Recursos", href: "/portal/recursos", icon: Users },
      { name: "Planos de Ação", href: "/portal/planos", icon: FileCheck },
    ]
  },
  {
    title: "Tecnologia",
    items: [
      { name: "Stack", href: "/portal/stack", icon: Code2 },
      { name: "Match", href: "/portal/match", icon: Target },
      { name: "Maturidade", href: "/portal/maturity", icon: Trophy },
    ]
  },
  {
    title: "Recursos",
    items: [
      { name: "Abrir Vaga", href: "/portal/vagas", icon: Briefcase },
    ]
  }
];

const portalSubMenu = portalMenuSections.flatMap(section => section.items);

const socialLinks = [
  { name: "LinkedIn", icon: Linkedin, href: "https://linkedin.com" },
  { name: "Instagram", icon: Instagram, href: "https://instagram.com" },
  { name: "YouTube", icon: Youtube, href: "https://youtube.com" },
];

const SIDEBAR_COLLAPSED_KEY = "openhub-sidebar-collapsed";

export function AppSidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isPortalUser, signOut: portalSignOut, clientInfo } = usePortalAuth();
  const { user, signOut: adminSignOut } = useAuth();
  const permissions = useUserPermissions();
  const { isAdmin } = useUserRole();
  const { hasModule, modules, currentModule, userModules } = useModuleAccess();

  // Get inactive sellable modules for lock indicators
  const inactiveModules = getInactiveModules(modules);

  // Use the extracted pure helpers, binding to the current module
  const isCurrentModuleItemBound = (item: { module?: ModuleKey }): boolean =>
    isCurrentModuleItem(item, currentModule);

  const isCurrentModuleSectionBound = (section: { module?: ModuleKey }): boolean =>
    isCurrentModuleSection(section, currentModule);

  // Filter menu items by active modules
  const filterByModule = <T extends { module?: ModuleKey }>(items: T[]): T[] =>
    items.filter((item) => !item.module || hasModule(item.module));

  const filterSection = (section: MenuSection): MenuSection | null => {
    if (section.module && !hasModule(section.module)) return null;
    const filteredItems = filterByModule(section.items);
    if (filteredItems.length === 0) return null;
    return { ...section, items: filteredItems };
  };
  const { logoWhiteUrl, platformName } = useBranding();
  
  const { isPlatformAdmin } = usePlatformAdmin();
  const { isPendingApproval, loading: trialLoading } = useTrialStatus();
  const { organizations, organizationName, switchOrganization } = useOrganization();

  // Fetch current user's team member profile
  const { data: userProfile } = useQuery({
    queryKey: ["sidebar-user-profile", user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      
      const { data: teamMember, error: teamError } = await db
        .from("professionals")
        .select("id, name, email, avatar_url, role")
        .eq("professional_type", "operacional")
        .eq("user_id", user.id)
        .maybeSingle();
      
      if (!teamError && teamMember) {
        return teamMember;
      }
      
      const { data: profile, error: profileError } = await db
        .from("profiles")
        .select("id, full_name, email, avatar_url")
        .eq("id", user.id)
        .maybeSingle();
      
      if (!profileError && profile) {
        return {
          id: profile.id,
          name: profile.full_name || user.email?.split("@")[0] || "Usuário",
          email: profile.email,
          avatar_url: profile.avatar_url,
          role: "admin",
        };
      }
      
      return {
        id: user.id,
        name: user.email?.split("@")[0] || "Usuário",
        email: user.email || "",
        avatar_url: null,
        role: "admin",
      };
    },
    enabled: !!user?.id && !isPortalUser,
  });

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .slice(0, 2)
      .toUpperCase();
  };
  
  const [collapsed, setCollapsed] = useState(() => {
    const saved = localStorage.getItem(SIDEBAR_COLLAPSED_KEY);
    return saved === "true";
  });
  
  useEffect(() => {
    localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(collapsed));
  }, [collapsed]);

  // Listen for external collapse requests (e.g. from pipeline page)
  useEffect(() => {
    const handler = () => setCollapsed(true);
    window.addEventListener("sidebar:collapse", handler);
    return () => window.removeEventListener("sidebar:collapse", handler);
  }, []);
  
  // Collapsible states
  
  const [timesheetOpen, setTimesheetOpen] = useState(location.pathname.startsWith("/timesheet"));
  const [culturaOpen, setCulturaOpen] = useState(location.pathname.startsWith("/cultura"));
  const [vagasOpen, setVagasOpen] = useState(location.pathname.startsWith("/vagas") || location.pathname.startsWith("/carreiras"));

  useEffect(() => {
    if (location.pathname.startsWith("/timesheet")) {
      setTimesheetOpen(true);
    }
    if (location.pathname.startsWith("/cultura")) {
      setCulturaOpen(true);
    }
    if (location.pathname.startsWith("/vagas") || location.pathname.startsWith("/carreiras")) {
      setVagasOpen(true);
    }
  }, [location.pathname]);

  const handlePortalLogout = async () => {
    await portalSignOut();
    navigate("/portal/login");
  };

  const handleAdminLogout = async () => {
    try {
      await adminSignOut();
    } catch (error) {
      log.info("Logout completed");
    }
    navigate("/auth");
  };

  const isActiveRoute = (href: string, exact?: boolean) => {
    if (exact) {
      return location.pathname === href;
    }
    return location.pathname === href || location.pathname.startsWith(href + "/");
  };

  const isMenuActive = (items: MenuItem[]) => {
    return items.some(item => isActiveRoute(item.href, item.exact));
  };

  // Filter section items based on permissions AND current module
  const filterSectionItems = useMemo(() => (items: MenuItem[]) => {
    return items.filter(item => {
      // Filter by current module: only show items for currentModule or shell (no module)
      if (!isCurrentModuleItemBound(item)) return false;
      // Also ensure the module is active for the user
      if (item.module && !hasModule(item.module)) return false;
      // Then filter by permission feature flag
      if (item.featureKey && !permissions[item.featureKey]) return false;
      return true;
    });
  }, [permissions, hasModule, currentModule]);

  const filteredGestao = filterSectionItems(gestaoSection.items);
  const filteredRecrutamento = filterSectionItems(recrutamentoSection.items);
  const filteredOperacoes = filterSectionItems(operacoesSection.items);
  const filteredAcompanhamento = filterSectionItems(acompanhamentoSection.items);
  const filteredResultados = filterSectionItems(resultadosSection.items);
  const filteredComercial = filterSectionItems(comercialSection.items);
  const filteredFinanceiro = filterSectionItems(financeiroSection.items);
  const filteredVagasItems = filterSectionItems(vagasItems);
  const filteredCulturaItems = filterSectionItems(culturaItems);
  const filteredTimesheetItems = filterSectionItems(isAdmin ? timesheetManagerItems : timesheetItems);
  

  // Render single nav item
  const renderNavItem = (item: MenuItem) => {
    const isActive = isActiveRoute(item.href, item.exact);
    const linkContent = (
      <NavLink
        to={item.href}
        className={cn(
          "group flex items-center gap-3 px-3 py-2 text-[13px] font-medium rounded-lg transition-all duration-200",
          item.comingSoon
            ? "text-sidebar-foreground/40 cursor-default"
            : isActive
              ? "bg-sidebar-primary text-sidebar-primary-foreground shadow-sm"
              : "text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent/50",
          collapsed && "justify-center px-2"
        )}
        onClick={item.comingSoon ? (e) => e.preventDefault() : undefined}
      >
        <item.icon className={cn(
          "h-4 w-4 shrink-0 transition-colors",
          isActive && "text-sidebar-primary-foreground"
        )} />
        {!collapsed && (
          <span className="flex-1 truncate">{item.name}</span>
        )}
        {!collapsed && item.comingSoon && (
          <span className="text-[9px] px-1.5 py-0.5 rounded bg-sidebar-accent/60 text-sidebar-foreground/50 uppercase font-semibold tracking-wide">
            breve
          </span>
        )}
      </NavLink>
    );

    if (collapsed) {
      return (
        <Tooltip key={item.name} delayDuration={0}>
          <TooltipTrigger asChild>
            {linkContent}
          </TooltipTrigger>
          <TooltipContent side="right" className="font-medium flex items-center gap-2">
            {item.name}
            {item.comingSoon && <span className="text-muted-foreground text-xs">(Em breve)</span>}
          </TooltipContent>
        </Tooltip>
      );
    }

    return <div key={item.name}>{linkContent}</div>;
  };

  // Render collapsible sub-menu
  const renderCollapsibleMenu = (
    title: string,
    Icon: React.ElementType,
    items: MenuItem[],
    isOpen: boolean,
    setIsOpen: (open: boolean) => void
  ) => {
    const isActive = isMenuActive(items);
    
    if (collapsed) {
      return (
        <Tooltip key={title} delayDuration={0}>
          <TooltipTrigger asChild>
            <NavLink
              to={items[0]?.href || "#"}
              className={cn(
                "flex items-center justify-center px-2 py-2 rounded-lg transition-all duration-200",
                isActive
                  ? "bg-sidebar-primary/20 text-sidebar-primary"
                  : "text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent/50"
              )}
            >
              <Icon className="h-4 w-4" />
            </NavLink>
          </TooltipTrigger>
          <TooltipContent side="right" className="font-medium">
            {title}
          </TooltipContent>
        </Tooltip>
      );
    }
    
    return (
      <Collapsible key={title} open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleTrigger asChild>
          <button
            className={cn(
              "w-full flex items-center gap-3 px-3 py-2 text-[13px] font-medium rounded-lg transition-all duration-200",
              isActive
                ? "bg-sidebar-primary/15 text-sidebar-primary"
                : "text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent/50"
            )}
          >
            <Icon className="h-4 w-4 shrink-0" />
            <span className="flex-1 text-left truncate">{title}</span>
            <ChevronDown 
              className={cn(
                "h-3.5 w-3.5 transition-transform duration-200 text-sidebar-foreground/40",
                isOpen && "rotate-180"
              )} 
            />
          </button>
        </CollapsibleTrigger>
        
        <CollapsibleContent className="overflow-hidden data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down">
          <div className="mt-1 ml-3 pl-3 border-l border-sidebar-border/50 space-y-0.5">
            {items.map((item) => {
              const isItemActive = isActiveRoute(item.href, item.exact);
              return (
                <NavLink
                  key={item.name}
                  to={item.href}
                  className={cn(
                    "flex items-center gap-2.5 px-2.5 py-1.5 text-[12px] font-medium rounded-md transition-all duration-200",
                    isItemActive
                      ? "bg-sidebar-primary text-sidebar-primary-foreground"
                      : "text-sidebar-foreground/60 hover:text-sidebar-foreground hover:bg-sidebar-accent/40"
                  )}
                >
                  <item.icon className="h-3.5 w-3.5 shrink-0" />
                  <span className="truncate">{item.name}</span>
                </NavLink>
              );
            })}
          </div>
        </CollapsibleContent>
      </Collapsible>
    );
  };

  // Render section header
  const renderSectionHeader = (title: string) => {
    if (collapsed) {
      return (
        <div className="my-2 mx-2 h-px bg-sidebar-border/30 first:hidden" aria-hidden />
      );
    }
    return (
      <div className="pt-5 pb-1.5 first:pt-0">
        <span className="px-3 text-[10px] uppercase tracking-widest text-sidebar-foreground/40 font-semibold">
          {title}
        </span>
      </div>
    );
  };

  // Portal sidebar
  if (isPortalUser) {
    return (
      <aside
        className={cn(
          "hidden md:flex flex-col bg-sidebar text-sidebar-foreground transition-all duration-300 ease-in-out shrink-0 sticky top-0 h-screen z-40 border-r border-sidebar-border/30",
          collapsed ? "w-16" : "w-60"
        )}
      >
        {/* Header */}
        <div className="flex flex-col items-center justify-center pt-6 pb-4 px-4 shrink-0">
          {!collapsed ? (
            <div className="flex flex-col items-center gap-2">
              <img src={logoWhiteUrl} alt={platformName} className="h-10" />
              <div className="flex items-center gap-1.5">
                <CircleDot className="h-2.5 w-2.5 text-accent" />
                <span className="text-[10px] text-accent font-semibold tracking-widest uppercase">
                  Portal
                </span>
              </div>
              {clientInfo?.name && (
                <span className="text-[10px] text-sidebar-foreground/50 truncate max-w-full">
                  {clientInfo.name}
                </span>
              )}
            </div>
          ) : (
            <div className="w-10 h-10 rounded-lg bg-accent flex items-center justify-center shadow-lg shadow-accent/30">
              <span className="text-accent-foreground font-bold text-sm">{platformName.charAt(0)}</span>
            </div>
          )}
        </div>

        {/* Portal Navigation */}
        <nav className="flex-1 px-2.5 py-2 overflow-y-auto min-h-0 sidebar-scroll-hidden">
          {collapsed ? (
            <div className="space-y-0.5">
              {portalSubMenu.map((item) => {
                const isActive = isActiveRoute(item.href, item.exact);
                return (
                  <Tooltip key={item.name} delayDuration={0}>
                    <TooltipTrigger asChild>
                      <NavLink
                        to={item.href}
                        className={cn(
                          "flex items-center justify-center px-2 py-2 rounded-lg transition-all duration-200",
                          isActive
                            ? "bg-sidebar-primary text-sidebar-primary-foreground"
                            : "text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent/50"
                        )}
                      >
                        <item.icon className="h-4 w-4" />
                      </NavLink>
                    </TooltipTrigger>
                    <TooltipContent side="right" className="font-medium">
                      {item.name}
                    </TooltipContent>
                  </Tooltip>
                );
              })}
            </div>
          ) : (
            <div className="space-y-3">
              {portalMenuSections.map((section, idx) => (
                <div key={section.title}>
                  {idx > 0 && <div className="my-2 h-px bg-sidebar-border/30" />}
                  <div className="pb-1">
                    <span className="px-2.5 text-[10px] uppercase tracking-widest text-sidebar-foreground/40 font-semibold">
                      {section.title}
                    </span>
                  </div>
                  <div className="space-y-0.5">
                    {section.items.map((item) => {
                      const isActive = isActiveRoute(item.href, item.exact);
                      return (
                        <NavLink
                          key={item.name}
                          to={item.href}
                          className={cn(
                            "flex items-center gap-2.5 px-2.5 py-2 text-[13px] font-medium rounded-lg transition-all duration-200",
                            isActive
                              ? "bg-sidebar-primary text-sidebar-primary-foreground"
                              : "text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent/50"
                          )}
                        >
                          <item.icon className="h-4 w-4 shrink-0" />
                          <span className="truncate">{item.name}</span>
                        </NavLink>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </nav>

        {/* Bottom Section */}
        <div className="p-2.5 space-y-1.5 shrink-0 border-t border-sidebar-border/30">
          {collapsed ? (
            <>
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={handlePortalLogout}
                    className="w-full h-9 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg"
                  >
                    <LogOut className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="right">Sair</TooltipContent>
              </Tooltip>
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setCollapsed(false)}
                    className="w-full h-9 text-sidebar-foreground/50 hover:text-sidebar-foreground hover:bg-sidebar-accent/50 rounded-lg"
                  >
                    <PanelLeft className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="right">Expandir</TooltipContent>
              </Tooltip>
            </>
          ) : (
            <>
              <Button
                variant="ghost"
                onClick={handlePortalLogout}
                className="w-full h-9 justify-start gap-2.5 px-2.5 text-[13px] font-medium text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg"
              >
                <LogOut className="h-4 w-4" />
                <span>Sair do Portal</span>
              </Button>
              <Button
                variant="ghost"
                onClick={() => setCollapsed(true)}
                className="w-full h-9 justify-start gap-2.5 px-2.5 text-[13px] font-medium text-sidebar-foreground/50 hover:text-sidebar-foreground hover:bg-sidebar-accent/50 rounded-lg"
              >
                <PanelLeftClose className="h-4 w-4" />
                <span>Recolher</span>
              </Button>
            </>
          )}
        </div>
      </aside>
    );
  }

  // Admin sidebar
  return (
    <aside
      className={cn(
        "hidden md:flex flex-col bg-sidebar text-sidebar-foreground transition-all duration-300 ease-in-out shrink-0 sticky top-0 h-screen z-40 border-r border-sidebar-border/30",
        collapsed ? "w-16" : "w-60"
      )}
    >
      {/* Header */}
      <div className={cn("flex flex-col items-center shrink-0", collapsed ? "pt-5 pb-3 px-2" : "pt-6 pb-4 px-4")}>
        {!collapsed ? (
          <div className="flex flex-col items-center gap-2 w-full">
            <img src="/landing/openhub-wordmark-white.png" alt={platformName} className="h-7 w-auto" />
            {organizations.length > 1 && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="text-[11px] text-sidebar-foreground/70 hover:text-sidebar-foreground gap-1 h-auto py-1 px-2">
                    <Building2 className="h-3 w-3" />
                    <span className="truncate max-w-[120px]">{organizationName}</span>
                    <ChevronDown className="h-3 w-3 shrink-0" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="center" className="w-52">
                  {organizations.map((org) => (
                    <DropdownMenuItem
                      key={org.organizationId}
                      onClick={() => {
                        switchOrganization(org.organizationId);
                        window.location.reload();
                      }}
                    >
                      <Building2 className="h-3.5 w-3.5 mr-2" />
                      <span className="truncate">{org.organizationName}</span>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        ) : (
          <img src="/landing/openhub-mark.svg" alt={platformName} className="h-7 w-7" />
        )}
      </div>

      {/* Navigation */}
      <nav className={cn("flex-1 py-2 overflow-y-auto min-h-0 sidebar-scroll-hidden", collapsed ? "px-1.5" : "px-2.5")}>
        {trialLoading ? (
          <div className="flex items-center justify-center h-full">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[var(--ink)]" />
          </div>
        ) : isPendingApproval ? (
          <div className="flex flex-col items-center justify-center h-full text-center px-4">
            {!collapsed && (
              <>
                <Clock className="h-8 w-8 text-muted-foreground/50 mb-3" />
                <p className="text-sm font-medium text-muted-foreground">Cadastro em análise</p>
                <p className="text-xs text-muted-foreground/60 mt-1">Aguardando aprovação para liberar o acesso.</p>
              </>
            )}
          </div>
        ) : (
          <>
        {/* Module Switcher — at the top of navigation */}
        <ModuleSwitcher collapsed={collapsed} />

        {/* Dashboard */}
        {permissions.visao_geral && renderNavItem(dashboardItem)}

        {/* GESTÃO */}
        {filteredGestao.length > 0 && (
          <>
            {renderSectionHeader("Gestão")}
            <div className="space-y-0.5">
              {filteredGestao.map(item => renderNavItem(item))}
            </div>
          </>
        )}

        {/* RECRUTAMENTO */}
        {isCurrentModuleSectionBound(recrutamentoSection) && hasModule("talent") && (filteredRecrutamento.length > 0 || (permissions.vagas && filteredVagasItems.length > 0)) && (
          <>
            {renderSectionHeader("Recrutamento")}
            <div className="space-y-0.5">
              {permissions.vagas && filteredVagasItems.length > 0 && renderCollapsibleMenu("Vagas", Briefcase, filteredVagasItems, vagasOpen, setVagasOpen)}
              {filteredRecrutamento.map(item => renderNavItem(item))}
            </div>
          </>
        )}

        {/* OPERAÇÕES */}
        {isCurrentModuleSectionBound(operacoesSection) && filteredOperacoes.length > 0 && (
          <>
            {renderSectionHeader("Operações")}
            <div className="space-y-0.5">
              {filteredOperacoes.map(item => renderNavItem(item))}
            </div>
          </>
        )}

        {/* ACOMPANHAMENTO — items are already filtered by currentModule via filterSectionItems */}
        {(filteredAcompanhamento.length > 0 || (permissions.cultura && filteredCulturaItems.length > 0) || (permissions.alocacoes && filteredTimesheetItems.length > 0)) && (
          <>
            {renderSectionHeader("Acompanhamento")}
            <div className="space-y-0.5">
              {filteredAcompanhamento.map(item => renderNavItem(item))}
              {permissions.cultura && filteredCulturaItems.length > 0 && renderCollapsibleMenu("Cultura", Heart, filteredCulturaItems, culturaOpen, setCulturaOpen)}
              {permissions.alocacoes && filteredTimesheetItems.length > 0 && renderCollapsibleMenu("Timesheet", Clock, filteredTimesheetItems, timesheetOpen, setTimesheetOpen)}
            </div>
          </>
        )}

        {/* RESULTADOS */}
        {isCurrentModuleSectionBound(resultadosSection) && (filteredResultados.length > 0 || permissions.metas) && (
          <>
            {renderSectionHeader("Resultados")}
            <div className="space-y-0.5">
              {filteredResultados.map(item => renderNavItem(item))}
              
            </div>
          </>
        )}

        {/* COMERCIAL */}
        {isCurrentModuleSectionBound(comercialSection) && filteredComercial.length > 0 && (
          <>
            {renderSectionHeader("Comercial")}
            <div className="space-y-0.5">
              {filteredComercial.map(item => renderNavItem(item))}
            </div>
          </>
        )}

        {/* FINANCEIRO */}
        {isCurrentModuleSectionBound(financeiroSection) && filteredFinanceiro.length > 0 && (
          <>
            {renderSectionHeader("Financeiro")}
            <div className="space-y-0.5">
              {filteredFinanceiro.map(item => renderNavItem(item))}
            </div>
          </>
        )}

        {/* CAMPEONATO — belongs to Talent module */}
        {currentModule === "talent" && permissions.opencode && (
          <>
            {renderSectionHeader("Campeonato")}
            <div className="space-y-0.5">
              {renderNavItem({
                name: "OpenCode",
                href: "/opencode/admin",
                icon: Trophy,
                featureKey: "opencode",
                module: "talent",
              })}
            </div>
          </>
        )}

        {/* LOCKED MODULES — upsell indicators */}
        {inactiveModules.length > 0 && (
          <>
            {renderSectionHeader("Módulos disponíveis")}
            <div className="space-y-0.5">
              {inactiveModules.map((modKey) => {
                const modNames: Record<string, string> = { talent: "Talent", sales: "Sales", operations: "Operations" };
                const modIcons: Record<string, React.ElementType> = { talent: Users, sales: Briefcase, operations: FolderKanban };
                const ModIcon = modIcons[modKey] || FolderKanban;
                const label = modNames[modKey] || modKey;

                if (collapsed) {
                  return (
                    <Tooltip key={modKey} delayDuration={0}>
                      <TooltipTrigger asChild>
                        <NavLink
                          to={`/pricing?module=${modKey}`}
                          className="flex items-center justify-center px-2 py-2 rounded-lg text-sidebar-foreground/30 hover:text-sidebar-foreground/50 hover:bg-sidebar-accent/30 transition-all duration-200"
                        >
                          <Lock className="h-4 w-4" />
                        </NavLink>
                      </TooltipTrigger>
                      <TooltipContent side="right" className="font-medium">
                        {label} — Ativar
                      </TooltipContent>
                    </Tooltip>
                  );
                }

                return (
                  <NavLink
                    key={modKey}
                    to={`/pricing?module=${modKey}`}
                    className="group flex items-center gap-3 px-3 py-2 text-[13px] font-medium rounded-lg text-sidebar-foreground/30 hover:text-sidebar-foreground/50 hover:bg-sidebar-accent/30 transition-all duration-200"
                  >
                    <Lock className="h-4 w-4 shrink-0" />
                    <span className="flex-1 truncate">{label}</span>
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-sidebar-accent/40 text-sidebar-foreground/40 uppercase font-semibold tracking-wide">
                      Ativar
                    </span>
                  </NavLink>
                );
              })}
            </div>
          </>
        )}

        {/* SUPER ADMIN */}
        {isPlatformAdmin && (
          <>
            {renderSectionHeader("Super Admin")}
            <div className="space-y-0.5">
              {renderNavItem({
                name: "Painel Admin",
                href: "/admin/painel",
                icon: LayoutDashboard,
                exact: true,
              })}
            </div>
          </>
        )}
          </>
        )}
      </nav>

      {/* Bottom Section */}
      <div className="p-2.5 space-y-1.5 shrink-0 border-t border-sidebar-border/30">
        {/* User Profile */}
        {userProfile && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              {collapsed ? (
                <button className="flex items-center justify-center p-1.5 rounded-lg hover:bg-sidebar-accent/50 transition-colors w-full">
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={userProfile.avatar_url || undefined} />
                    <AvatarFallback className="bg-sidebar-primary text-sidebar-primary-foreground text-xs font-medium">
                      {getInitials(userProfile.name)}
                    </AvatarFallback>
                  </Avatar>
                </button>
              ) : (
                <button className="flex items-center gap-2.5 px-2.5 py-2 rounded-lg hover:bg-sidebar-accent/50 transition-colors w-full text-left">
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={userProfile.avatar_url || undefined} />
                    <AvatarFallback className="bg-sidebar-primary text-sidebar-primary-foreground text-xs font-medium">
                      {getInitials(userProfile.name)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <p className="text-[13px] font-medium text-sidebar-foreground truncate">{userProfile.name}</p>
                    <p className="text-[11px] text-sidebar-foreground/50 truncate">{userProfile.email}</p>
                  </div>
                </button>
              )}
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" side="right" className="w-52">
              {permissions.configuracoes && (
                <DropdownMenuItem onClick={() => navigate("/configuracoes")} className="text-[13px]">
                  <Settings className="h-4 w-4 mr-2" />
                  Configurações
                </DropdownMenuItem>
              )}
              {permissions.configuracoes && <DropdownMenuSeparator />}
              <DropdownMenuItem onClick={handleAdminLogout} className="text-red-600 text-[13px]">
                <LogOut className="h-4 w-4 mr-2" />
                Sair
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        {/* Collapse Toggle */}
        {collapsed ? (
          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setCollapsed(false)}
                className="w-full h-9 text-sidebar-foreground/50 hover:text-sidebar-foreground hover:bg-sidebar-accent/50 rounded-lg"
              >
                <PanelLeft className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="right">Expandir</TooltipContent>
          </Tooltip>
        ) : (
          <Button
            variant="ghost"
            onClick={() => setCollapsed(true)}
            className="w-full h-9 justify-start gap-2.5 px-2.5 text-[13px] font-medium text-sidebar-foreground/50 hover:text-sidebar-foreground hover:bg-sidebar-accent/50 rounded-lg"
          >
            <PanelLeftClose className="h-4 w-4" />
            <span>Recolher</span>
          </Button>
        )}
      </div>

      {/* Social Links */}
      {!collapsed && (
        <div className="px-2.5 py-3 shrink-0">
          <div className="flex items-center justify-center gap-2">
            {socialLinks.map((social) => (
              <a
                key={social.name}
                href={social.href}
                target="_blank"
                rel="noopener noreferrer"
                className="w-8 h-8 rounded-lg bg-sidebar-accent/30 flex items-center justify-center text-sidebar-foreground/50 hover:text-accent hover:bg-sidebar-accent/60 transition-all duration-200"
                title={social.name}
              >
                <social.icon className="h-3.5 w-3.5" />
              </a>
            ))}
          </div>
        </div>
      )}
    </aside>
  );
}
